import {
  Box,
  Grid,
  TextField,
  Typography,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Divider,
  Autocomplete
} from '@mui/material';
import { useEffect, useState } from 'react';
import { api } from '../../api';
import { ActiveStatus, FitToWork, WorkPermit } from '../../schema/ptt';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import dayjs from 'dayjs';
import { Area } from '../../schema/area';

interface WorkPermitProps {
  person_id?: string | number;
  editable?: boolean;
  reloadStatus?: () => void;
}

const WorkPermitPage: React.FC<WorkPermitProps> = ({
  person_id,
  editable,
  reloadStatus
}: WorkPermitProps) => {
  const [activeStatus, setActiveStatus] = useState<ActiveStatus | null>(null);
  const [wpmList, setWPMList] = useState<WorkPermit[]>([]);

  const [expands, setExpands] = useState<Record<number, boolean>>({});
  const [isOpenSuccessDialog, setIsOpenSuccessDialog] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [areasC, setAreasC] = useState<Area[]>([]);

  

  const toggleExpands = (index: number) => {
    const isExpand = expands[index] ? expands[index] : false;
    setExpands({ ...expands, [index]: !isExpand });
  };

  const openSuccessDialog = () => {
    setIsOpenSuccessDialog(true);
    setTimeout(() => {
      setIsOpenSuccessDialog(false);
    }, 1000);
  };

  const saveTempStatus = () => {
    if (!editable) {
      return;
    }
    if (isLoading) {
      return;
    }
    if (person_id && activeStatus) {
      setIsLoading(true);
      api
        .post(`person/${person_id}/temporary_status`, {
          is_wpm_active: activeStatus.is_temp_active,
          wpm_comment: activeStatus.comment
        })
        .then(() => {
          openSuccessDialog();
          reloadStatus && reloadStatus();
        })
        .finally(() => setIsLoading(false));
    }
  };

  useEffect(() => {
    if (person_id) {
      api.get(`/person/${person_id}/wpm`).then((res) => {
        const { data } = res;
        const temp_data = data;
        for (let i = 0; i < temp_data.length; i++) {
          const element = temp_data[i];
          api.get(`staff`, { params: { employee_code: element.supervisorCode } }).then((res) => {
            const { data } = res;
            temp_data[i]["supervisor"] = data[0];
            if (i == (temp_data.length-1)) {
              setWPMList(temp_data)
            }
          });
        }
      });

      api.get(`person/${person_id}/wpm/status`).then((res) => {
        const { data } = res;
        setActiveStatus(data);
      });

      api.get<Area[]>(`/area`).then((res) => {
        const { data } = res;
        setAreasC(data.filter((data) => data.is_require_wpm === true));
      });
    }
  }, [person_id]);

  return (
    <>
      <Typography variant="h6">การอนุญาต</Typography>
      {activeStatus?.is_active ? (
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="สถานะการอนุญาต"
                variant="outlined"
                placeholder="สถานะการอนุญาต"
                value={'ผ่านโดยระบบ'}
                // sx={{ input: { color: 'green' } }}
                autoComplete="off"
                InputProps={{
                  readOnly: true
                }}
              />
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              {editable ? (
                <FormControl fullWidth>
                  <InputLabel id="is_active">สถานะการอนุญาต</InputLabel>
                  <Select
                    labelId="is_active"
                    label="สถานะการอนุญาต"
                    variant="outlined"
                    onChange={(e) =>
                      activeStatus
                        ? setActiveStatus({
                            ...activeStatus,
                            is_temp_active: e.target.value == 'true'
                          })
                        : null
                    }
                    value={`${activeStatus?.is_temp_active === true}`}
                    autoComplete="off"
                    disabled={!activeStatus}
                  >
                    <MenuItem value={'true'}>ผ่าน</MenuItem>
                    <MenuItem value={'false'}>ไม่ผ่าน</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  fullWidth
                  label="สถานะการอนุญาต"
                  variant="outlined"
                  placeholder="สถานะการอนุญาต"
                  value={activeStatus?.is_temp_active ? 'ผ่าน' : 'ไม่ผ่าน' + (wpmList.length > 0 ? ' (อาจมีรายการที่ร้องขอ FTW)' : '')}
                  autoComplete="off"
                  InputProps={{
                    readOnly: true
                  }}
                />
              )}
            </Grid>
            <Grid item xs={9}>
              <TextField
                fullWidth
                label="หมายเหตุการอนุญาตชั่วคราวโดยเจ้าหน้าที่"
                variant="outlined"
                placeholder="หมายเหตุการอนุญาตชั่วคราวโดยเจ้าหน้าที่"
                onChange={(e) =>
                  activeStatus
                    ? setActiveStatus({ ...activeStatus, comment: e.target.value })
                    : null
                }
                value={activeStatus?.comment ? activeStatus.comment : ''}
                autoComplete="off"
                disabled={!activeStatus}
              />
            </Grid>
          </Grid>
          {editable ? (
            <Grid container columns={2} spacing={2} sx={{ mt: 0 }} direction={'row-reverse'}>
              <Grid item>
                <Button onClick={() => saveTempStatus()} variant="contained" color="primary">
                  บันทึก
                </Button>
              </Grid>
            </Grid>
          ) : null}
        </Box>
      )}
      <Divider sx={{ mt: 3 }} />
      {wpmList.length == 0 ? (
        <Typography variant="h6" mt={3} sx={{ color: "red" }}>
          ไม่พบข้อมูล Work Permit หรือไม่ได้อยู้ในช่วงเวลาของใบงาน
          <Typography variant="subtitle2" mt={1} sx={{ color: "black" }}>
              กรุณาตรวจสอบข้อมูลผ่านระบบ Work Permit
            </Typography>
        </Typography>
      ) : (
        <>
          <Typography variant="h6" mt={3}>
            ข้อมูล Work permit
          </Typography>
          <Box sx={{ mt: 2 }}>
            {wpmList.map((wpm, index) => {
              const ftw: FitToWork | undefined = wpm.ftw;
              let bp = null;
              let alcohol = null;
              let drug = null;
              // let SYSTOLIC = null;
              // let DIASTOLIC = null;
              // let PULSE = null;
              // let TEMP = null;
              // let ALCOHOL = null;
              // let DRUG = null;
              if (ftw) {
                bp = ftw.Measurements.find((data) => data.Name == 'BP');
                alcohol = ftw.Measurements.find((data) => data.Name == 'Alcohol');
                drug = ftw.Measurements.find((data) => data.Name == 'Drug');
                // SYSTOLIC = ftw.Measurements.find((data) => data.Name == 'SYSTOLIC');
                // DIASTOLIC = ftw.Measurements.find((data) => data.Name == 'DIASTOLIC');
                // PULSE = ftw.Measurements.find((data) => data.Name == 'PULSE');
                // TEMP = ftw.Measurements.find((data) => data.Name == 'TEMP');
                // ALCOHOL = ftw.Measurements.find((data) => data.Name == 'ALCOHOL');
                // DRUG = ftw.Measurements.find((data) => data.Name == 'DRUG');
              }

              return (
                <Card sx={{ marginBottom: 3, border: 1, borderColor: '#E5E5E5' }} key={index}>
                  <CardContent
                    sx={{
                      p: 2,
                      '&:last-child': {
                        paddingBottom: 2
                      }
                    }}
                  >
                    <Grid
                      container
                      alignItems="center"
                      onClick={() => toggleExpands(index)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <Grid item xs={10}>
                        <Typography variant="h6">
                          {wpm?.workPermitNo ? `Work Permit : ${wpm.workPermitNo} (${wpm.workPermitID})` : 'Work Permit'}
                        </Typography>
                      </Grid>
                      <Grid item xs={2} sx={{ textAlign: 'right' }}>
                        <IconButton onClick={() => toggleExpands(index)}>
                          {!expands[index] ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
                        </IconButton>
                      </Grid>
                    </Grid>
                    <Collapse in={expands[index]}>
                      <Box sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Description"
                              variant="outlined"
                              placeholder="Description"
                              value={wpm?.description ? wpm.description : ''}
                              InputProps={{ readOnly: true }}
                              disabled={!wpm?.description}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="ประเภทงาน"
                              variant="outlined"
                              placeholder="ประเภทงาน"
                              value={wpm?.workType ? wpm?.workType : ''}
                              InputProps={{ readOnly: true }}
                              disabled={!wpm?.workType}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="วันเริ่ม Work Permit"
                              variant="outlined"
                              placeholder="วันเริ่ม"
                              value={
                                wpm?.workStartDate
                                  ? dayjs(wpm.workStartDate).format('DD/MM/YYYY')
                                  : ''
                              }
                              InputProps={{ readOnly: true }}
                              disabled={!wpm?.workStartDate}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="วันสิ่นสุด Work Permit"
                              variant="outlined"
                              placeholder="วันสิ่นสุด"
                              value={
                                wpm?.workEndDate ? dayjs(wpm.workEndDate).format('DD/MM/YYYY') : ''
                              }
                              InputProps={{ readOnly: true }}
                              disabled={!wpm?.workEndDate}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="เวลาเริ่มงาน"
                              variant="outlined"
                              placeholder="เวลาเริ่มงาน"
                              value={wpm?.workStartTime ? wpm?.workStartTime.substring(0, 5) : ''}
                              InputProps={{ readOnly: true }}
                              disabled={!wpm?.workStartTime}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="เวลาเลิกงาน"
                              variant="outlined"
                              placeholder="เวลาเลิกงาน"
                              value={wpm?.workEndTime ? wpm?.workEndTime.substring(0, 5) : ''}
                              InputProps={{ readOnly: true }}
                              disabled={!wpm?.workEndTime}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="ผู้ควบคุมงาน"
                              variant="outlined"
                              placeholder="ผู้ควบคุมงาน"
                              value={wpm?.supervisor?.Person.first_name ? (wpm?.supervisor?.Person.first_name + " " + wpm?.supervisor?.Person.last_name) : ''}
                              InputProps={{ readOnly: true }}
                              disabled={!wpm?.supervisor?.Person.first_name}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="หน่วยงานของผู้ควบคุมงาน"
                              variant="outlined"
                              placeholder="หน่วยงานของผู้ควบคุมงาน"
                              value={wpm?.supervisor?.department_name ? (wpm?.supervisor?.department_name) : ''}
                              InputProps={{ readOnly: true }}
                              disabled={!wpm?.supervisor?.department_name}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="เบอร์โทรศัพท์ผู้ควบคุมงาน"
                              variant="outlined"
                              placeholder="เบอร์โทรศัพท์ผู้คุมงาน"
                              value={wpm?.supervisor?.Person.phone_number ? wpm?.supervisor.Person.phone_number : ''}
                              InputProps={{ readOnly: true }}
                              disabled={!wpm?.supervisor?.Person.phone_number}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Email ผู้ควบคุมงาน"
                              variant="outlined"
                              placeholder="Email ผู้คุมงาน"
                              value={wpm?.supervisor?.Person.email ? wpm?.supervisor.Person.email : ''}
                              InputProps={{ readOnly: true }}
                              disabled={!wpm?.supervisor?.Person.email}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <Autocomplete
                                multiple
                                disablePortal
                                options={areasC}
                                getOptionLabel={(option) =>
                                  typeof option != 'string' ? option.area_name : option
                                }
                                renderInput={(params) => (
                                  <TextField {...params} label="พื้นที่อนุญาต" />
                                )}
                                value={areasC.filter((data) => data.area_code == wpm?.locationID)}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                readOnly
                                freeSolo
                              />
                          </Grid>
                        </Grid>
                      </Box>
                      <Typography variant="h6" sx={{ mt: 2 }}>
                        Fit To Work
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Fit To Work"
                              variant="outlined"
                              placeholder="Fit To Work"
                              value={
                                wpm?.fitToWorkRequired == undefined
                                  ? ''
                                  : wpm?.fitToWorkRequired
                                  ? 'ต้องการ'
                                  : 'ไม่ต้องการ'
                              }
                              InputProps={{ readOnly: true }}
                              disabled={wpm?.fitToWorkRequired == undefined}
                            />
                          </Grid>
                          {wpm?.fitToWorkRequired ? (
                            <>
                              <Grid item xs={6}>
                                <TextField
                                  fullWidth
                                  label="ผลทดสอบ Fit To Work"
                                  variant="outlined"
                                  placeholder="ผลทดสอบ Fit To Work"
                                  value={
                                    !wpm?.fitToWorkRequired
                                      ? ''
                                      : ftw?.EvaluationResult == 'N'
                                      ? 'ปกติ'
                                      : ftw?.EvaluationResult == 'W'
                                      ? 'เฝ้าระวัง'
                                      : ftw?.EvaluationResult == 'F'
                                      ? 'มีความเสียง'
                                      : 'ไม่ผ่าน'
                                  }
                                  sx={{
                                    input: {
                                      color:
                                        ftw?.EvaluationResult == 'N'
                                          ? 'Green'
                                          : ftw?.EvaluationResult == 'W'
                                          ? 'warning.main'
                                          : ftw?.EvaluationResult == 'F'
                                          ? 'Red'
                                          : 'Red'
                                    }
                                  }}
                                  InputProps={{ readOnly: true }}
                                  disabled={!ftw?.EvaluationResult || !ftw?.EvaluationResult}
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <TextField
                                  fullWidth
                                  label="เวลาสิ้นสุดผลทดสอบ"
                                  variant="outlined"
                                  placeholder="เวลาสิ้นสุดผลทดสอบ"
                                  value={
                                    ftw?.Expried
                                      ? dayjs(ftw.Expried).format('DD/MM/YYYY HH:mm')
                                      : ''
                                  }
                                  InputProps={{ readOnly: true }}
                                  disabled={!ftw?.Expried}
                                  sx={{
                                    input: {
                                      color: dayjs(ftw?.Expried) < dayjs() ? 'red' : ''
                                    }
                                  }}
                                />
                              </Grid>
                              {bp ? (
                                <>
                                  <Grid item xs={3}>
                                    <TextField
                                      fullWidth
                                      label="ความดันโลหิตตัวบน (SYS)"
                                      variant="outlined"
                                      placeholder="mm/Hg"
                                      value={`${
                                        bp.Value
                                          ? bp.Value.split('/').length > 0
                                            ? bp.Value?.split('/')[0]
                                            : '-'
                                          : '-'
                                      } ${bp.Unit} | ${
                                        bp.RegulartionType == 'N'
                                          ? 'ปกติ'
                                          : bp.RegulartionType == 'W'
                                          ? 'อยู่ในเกณฑ์เฝ้าระวัง'
                                          : bp.RegulartionType == 'F'
                                          ? 'ความดันสูง'
                                          : 'ไม่มีข้อมูล'
                                      }`} // N = ปกติ (สีเขียว) W = อยู่ในเกณฑ์เฝ้าระวัง (สีเหลือง)  F = ความดันสูง (สีแดง)
                                      InputProps={{ readOnly: true }}
                                      disabled={!bp}
                                      sx={{
                                        input: {
                                          color:
                                            bp.RegulartionType == 'N'
                                              ? 'green'
                                              : bp.RegulartionType == 'W'
                                              ? 'warning.main'
                                              : bp.RegulartionType == 'F'
                                              ? 'red'
                                              : 'gray'
                                        }
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={3}>
                                    <TextField
                                      fullWidth
                                      label="ความดันโลหิตตัวล่าง (DIA)"
                                      variant="outlined"
                                      placeholder="mm/Hg"
                                      value={`${
                                        bp.Value
                                          ? bp.Value.split('/').length > 1
                                            ? bp.Value?.split('/')[1]
                                            : '-'
                                          : '-'
                                      } ${bp.Unit} | ${
                                        bp.RegulartionType == 'N'
                                          ? 'ปกติ'
                                          : bp.RegulartionType == 'W'
                                          ? 'อยู่ในเกณฑ์เฝ้าระวัง'
                                          : bp.RegulartionType == 'F'
                                          ? 'ความดันสูง'
                                          : 'ไม่มีข้อมูล'
                                      }`} // N = ปกติ (สีเขียว) W = อยู่ในเกณฑ์เฝ้าระวัง (สีเหลือง)  F = ความดันสูง (สีแดง)
                                      InputProps={{ readOnly: true }}
                                      disabled={!bp}
                                      sx={{
                                        input: {
                                          color:
                                            bp.RegulartionType == 'N'
                                              ? 'green'
                                              : bp.RegulartionType == 'W'
                                              ? 'warning.main'
                                              : bp.RegulartionType == 'F'
                                              ? 'red'
                                              : 'gray'
                                        }
                                      }}
                                    />
                                  </Grid>
                                </>
                              ) : null}

                              {/* <Grid item xs={3}>
                          <TextField
                            fullWidth
                            label="ความดันโลหิตตัวล่าง (DIA)"
                            variant="outlined"
                            placeholder="mm/Hg"
                            value="80 mm/Hg | ปกติ" // N = ปกติ (สีเขียว) W = อยู่ในเกณฑ์เฝ้าระวัง (สีเหลือง)  F = ความดันสูง (สีแดง)
                            InputProps={{ readOnly: true }}
                            disabled={!ftw?.EvaluationResult || !ftw?.EvaluationResult}
                            sx={{ input: { color: 'green' } }}
                          />
                        </Grid> */}
                              {/* <Grid item xs={3}>
                          <TextField
                            fullWidth
                            label="อัตราการเต้นของหัวใจ"
                            variant="outlined"
                            placeholder="bpm"
                            value="88 bpm | ปกติ" // N = ปกติ (สีเขียว) W = อยู่ในเกณฑ์เฝ้าระวัง (สีเหลือง)  F = ความดันสูง (สีแดง)
                            InputProps={{ readOnly: true }}
                            disabled={!ftw?.EvaluationResult || !ftw?.EvaluationResult}
                            sx={{ input: { color: 'green' } }}
                          />
                        </Grid> */}
                              {/* <Grid item xs={3}>
                          <TextField
                            fullWidth
                            label="อุณหภูมิร่างกาย"
                            variant="outlined"
                            placeholder="°C"
                            value="36.7 °C | ปกติ" // N = ปกติ (สีเขียว) W = อยู่ในเกณฑ์เฝ้าระวัง (สีเหลือง)  F = ความดันสูง (สีแดง)
                            InputProps={{ readOnly: true }}
                            disabled={!ftw?.EvaluationResult || !ftw?.EvaluationResult}
                            sx={{ input: { color: 'green' } }}
                          />
                        </Grid> */}
                              {/* {alcohol ? (
                          <Grid item xs={3}>
                            <TextField
                              fullWidth
                              label="ระดับแอลกอฮอล์ในเลือด"
                              variant="outlined"
                              placeholder="ระดับแอลกอฮอล์ในเลือด"
                              value={alcohol.RegulartionType == 'N' ? 'ปกติ' : 'ไม่ผ่าน'}
                              InputProps={{ readOnly: true }}
                              disabled={!alcohol}
                              sx={{ input: { color: alcohol.RegulartionType == 'N' ? 'green' : 'red' } }}
                            />
                          </Grid>
                        ) : null}

                        {drug ? (
                          <Grid item xs={3}>
                            <TextField
                              fullWidth
                              label="สารเสพติดในเลือด"
                              variant="outlined"
                              placeholder="สารเสพติดในเลือด"
                              value={drug.RegulartionType == 'N' ? 'ปกติ' : 'ไม่ผ่าน'}
                              InputProps={{ readOnly: true }}
                              disabled={!drug}
                              sx={{ input: { color: drug.RegulartionType == 'N' ? 'green' : 'red' } }}
                            />
                          </Grid>
                        ) : null} */}
                            </>
                          ) : (
                            ''
                          )}
                        </Grid>
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </>
      )}
      <Dialog open={isOpenSuccessDialog} onClose={() => setIsOpenSuccessDialog(false)}>
        <DialogTitle>{'Success'}</DialogTitle>
        <DialogContent>
          <DialogContentText>อัพเดทข้อมูลการตรวจสอบและอัปเดตบุคคลสำเร็จ</DialogContentText>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WorkPermitPage;
